import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg, useSeo } from "../lib/api";
import { useAuth, isStaff } from "../context/AppContext";
import { Logo } from "../components/Logo";

const Shell = ({ title, sub, children }) => (
  <div className="min-h-[80vh] grid lg:grid-cols-2">
    <div className="hidden lg:flex bg-zinc-950 text-white grid-lines p-12 flex-col justify-between">
      <Logo light />
      <div><span className="eyebrow">Kursiyer Platformu</span><h2 className="mt-3 text-4xl font-black leading-tight">Eğitiminiz, sınavlarınız ve ilerlemeniz tek panelde.</h2></div>
      <p className="text-xs text-zinc-500">PROFESYONEL EĞİTİM • GÜNCEL BİLGİ • UYGULAMALI EĞİTİM</p>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-black" data-testid="auth-title">{title}</h1>
        {sub && <p className="text-sm text-zinc-600 mt-2">{sub}</p>}
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const Err = ({ e }) => e ? <p className="border-l-4 border-[#dc2626] bg-red-50 text-red-800 px-4 py-3 text-sm" role="alert" data-testid="auth-error">{e}</p> : null;
const Ok = ({ m }) => <div className="border-2 border-green-600 bg-green-50 p-5 text-sm flex gap-3" data-testid="auth-success"><CheckCircle2 className="text-green-700 shrink-0" />{m}</div>;

function useForm(init) {
  const [f, setF] = useState(init);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return [f, set];
}

export function Login() {
  const [f, set] = useForm({ identifier: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  useSeo("Kursiyer Girişi", "Ada Özel Güvenlik Eğitim Kurumu kursiyer paneli girişi.");
  useEffect(() => { if (user) nav(isStaff(user) ? "/yonetim" : "/kursiyer-paneli", { replace: true }); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.post("/auth/login", f); setUser(data); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Kursiyer Girişi" sub="E-posta adresiniz veya telefon numaranız ile giriş yapın.">
      <form onSubmit={submit} className="grid gap-4" data-testid="login-form">
        <div><label className="label" htmlFor="l-id">E-posta veya telefon</label><input id="l-id" className="field" autoComplete="username" required value={f.identifier} onChange={set("identifier")} data-testid="login-identifier" /></div>
        <div><label className="label" htmlFor="l-pw">Şifre</label><input id="l-pw" type="password" className="field" autoComplete="current-password" required value={f.password} onChange={set("password")} data-testid="login-password" /></div>
        <Err e={err} />
        <button className="btn-red" disabled={busy} data-testid="login-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Giriş Yap</button>
        <div className="flex justify-between text-sm"><Link to="/sifremi-unuttum" className="underline" data-testid="login-forgot-link">Şifremi unuttum</Link><Link to="/kayit-ol" className="font-bold text-[#dc2626]" data-testid="login-register-link">Hesap oluştur</Link></div>
      </form>
    </Shell>
  );
}

export function Register() {
  const [f, set] = useForm({ name: "", email: "", phone: "", password: "", password2: "", kvkk_ack: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  useSeo("Kursiyer Hesabı Oluştur", "Kursiyer paneline kayıt olun.");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 8) return setErr("Şifre en az 8 karakter olmalı.");
    if (f.password !== f.password2) return setErr("Şifreler eşleşmiyor.");
    if (!f.kvkk_ack) return setErr("Aydınlatma metnini onaylayın.");
    setBusy(true);
    try { const { password2, ...body } = f; const { data } = await api.post("/auth/register", body); setUser(data); nav("/kursiyer-paneli"); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Hesap Oluştur" sub="Kayıt sonrası e-posta adresinize doğrulama bağlantısı gönderilir.">
      <form onSubmit={submit} className="grid gap-4" data-testid="register-form">
        <div><label className="label" htmlFor="r-n">Ad soyad</label><input id="r-n" className="field" required value={f.name} onChange={set("name")} data-testid="register-name" /></div>
        <div><label className="label" htmlFor="r-e">E-posta</label><input id="r-e" type="email" className="field" required value={f.email} onChange={set("email")} data-testid="register-email" /></div>
        <div><label className="label" htmlFor="r-p">Telefon (telefonla giriş için)</label><input id="r-p" type="tel" className="field" value={f.phone} onChange={set("phone")} data-testid="register-phone" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label" htmlFor="r-pw">Şifre</label><input id="r-pw" type="password" className="field" autoComplete="new-password" required value={f.password} onChange={set("password")} data-testid="register-password" /></div>
          <div><label className="label" htmlFor="r-pw2">Şifre tekrar</label><input id="r-pw2" type="password" className="field" autoComplete="new-password" required value={f.password2} onChange={set("password2")} data-testid="register-password2" /></div>
        </div>
        <label className="flex gap-3 text-sm"><input type="checkbox" className="accent-[#dc2626] w-4 h-4 mt-0.5" checked={f.kvkk_ack} onChange={set("kvkk_ack")} data-testid="register-kvkk" /><span><Link to="/yasal/kvkk-aydinlatma-metni" target="_blank" className="underline">KVKK Aydınlatma Metni</Link>'ni okudum.</span></label>
        <Err e={err} />
        <button className="btn-red" disabled={busy} data-testid="register-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Kayıt Ol</button>
        <p className="text-sm">Hesabınız var mı? <Link to="/kursiyer-girisi" className="font-bold text-[#dc2626]">Giriş yapın</Link></p>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useSeo("Şifremi Unuttum");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { setMsg((await api.post("/auth/forgot-password", { email })).data.message); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Şifremi Unuttum" sub="Kayıtlı e-posta adresinizi girin; size 1 saat geçerli bir sıfırlama bağlantısı gönderelim.">
      {msg ? <Ok m={msg} /> : (
        <form onSubmit={submit} className="grid gap-4" data-testid="forgot-form">
          <div><label className="label" htmlFor="f-e">E-posta</label><input id="f-e" type="email" className="field" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
          <Err e={err} />
          <button className="btn-red" disabled={busy} data-testid="forgot-submit-btn">{busy && <Loader2 size={16} className="animate-spin" />}Bağlantı Gönder</button>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  useSeo("Şifre Sıfırla");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8 || pw !== pw2) return setErr("Şifreler en az 8 karakter olmalı ve eşleşmeli.");
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }); setOk(true); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Yeni Şifre Belirle">
      {ok ? <><Ok m="Şifreniz güncellendi." /><Link to="/kursiyer-girisi" className="btn-red mt-4 w-full">Giriş Yap</Link></> : (
        <form onSubmit={submit} className="grid gap-4" data-testid="reset-form">
          <div><label className="label" htmlFor="rs-1">Yeni şifre</label><input id="rs-1" type="password" className="field" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /></div>
          <div><label className="label" htmlFor="rs-2">Yeni şifre tekrar</label><input id="rs-2" type="password" className="field" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password2" /></div>
          <Err e={err} />
          <button className="btn-red" data-testid="reset-submit-btn">Şifreyi Güncelle</button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading");
  const [err, setErr] = useState("");
  const { refresh } = useAuth();
  useEffect(() => {
    api.post("/auth/verify-email", { token: params.get("token") || "" }).then(() => { setState("ok"); refresh(); }).catch((x) => { setErr(errMsg(x)); setState("err"); });
  }, [params, refresh]);
  return (
    <Shell title="E-posta Doğrulama">
      {state === "loading" ? <Loader2 className="animate-spin" /> : state === "ok" ? <><Ok m="E-posta adresiniz doğrulandı." /><Link to="/kursiyer-paneli" className="btn-red mt-4 w-full">Kursiyer Paneline Git</Link></> : <Err e={err} />}
    </Shell>
  );
}
